import React, { useState, useEffect } from 'react';
import { BsTrophy, BsPeople, BsPersonPlus, BsEye } from 'react-icons/bs';
import tournamentService, { Tournament } from '../../../services/tournamentService';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { ErrorMessage } from '../../../components/ui/ErrorMessage';

interface AdminTournamentsListProps {
  onCreateNew: () => void;
  onEditTournament: (tournament: Tournament) => void;
}

export const AdminTournamentsList: React.FC<AdminTournamentsListProps> = ({
  onCreateNew,
  onEditTournament
}) => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await tournamentService.getAdministrableTournaments();
      const tournamentList = Array.isArray(response) ? response : response.results;
      setTournaments(tournamentList);
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar los torneos';
      setError(errorMessage);
      console.error('Error loading tournaments:', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTournaments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta una vez al montar el componente

  const formatDate = (dateString: string) => {
    // Evitar problemas de zona horaria usando el string directamente
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '';
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (state?: string) => {
    if (!state) return null;
    
    const statusConfig = {
      PROGRAMMED: { color: 'bg-blue-100 text-blue-800', text: 'Programado' },
      STARTED: { color: 'bg-green-100 text-green-800', text: 'En curso' },
      FINISHED: { color: 'bg-gray-100 text-gray-800', text: 'Finalizado' },
    };

    const config = statusConfig[state as keyof typeof statusConfig];
    if (!config) return null;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Cargando torneos administrables..." />;
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error} 
        onRetry={loadTournaments}
        title="Error al cargar torneos administrables"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con botón crear nuevo */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mis Torneos</h2>
          <p className="text-gray-600">Gestiona los torneos que administras</p>
        </div>
        <button
          onClick={onCreateNew}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <BsPersonPlus className="h-5 w-5 mr-2" />
          Nuevo Torneo
        </button>
      </div>

      {/* Lista de torneos */}
      {tournaments.length === 0 ? (
        <div className="text-center py-12">
          <BsTrophy className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay torneos</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aún no administras ningún torneo. Crea tu primer torneo.
          </p>
          <button
            onClick={onCreateNew}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
          >
            <BsPersonPlus className="h-4 w-4 mr-2" />
            Crear Torneo
          </button>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {tournaments.map((tournament) => (
              <li key={tournament.id} className="hover:bg-gray-50">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {tournament.name}
                        </h3>
                        {getStatusBadge(tournament.state)}
                      </div>
                      
                      {tournament.description && (
                        <p className="mt-1 text-sm text-gray-600 truncate">
                          {tournament.description}
                        </p>
                      )}

                      <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          📅 {formatDate(tournament.date)}
                          {tournament.start_time && (
                            <>
                              🕐 {formatTime(tournament.start_time)}
                            </>
                          )}
                        </div>

                        {tournament.current_players !== undefined && tournament.max_players && (
                          <div className="flex items-center">
                            <BsPeople className="h-4 w-4 mr-1" />
                            {tournament.current_players}/{tournament.max_players} jugadores
                          </div>
                        )}

                        {tournament.format && (
                          <div className="flex items-center">
                            <BsTrophy className="h-4 w-4 mr-1" />
                            {tournament.format.charAt(0).toUpperCase() + tournament.format.slice(1)}
                          </div>
                        )}

                        {tournament.location && (
                          <div className="flex items-center">
                            📍 {tournament.location}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="ml-6 flex items-center space-x-2">
                      <button
                        onClick={() => window.open(`/tournaments/${tournament.id}`, '_blank')}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        title="Ver torneo"
                      >
                        <BsEye className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => onEditTournament(tournament)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        title="Editar torneo"
                      >
                        ✏️ Editar
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
